import { useCallback, useEffect, useMemo, useState } from 'react';
import { SystemPermissionResult } from '../../system/permissions';
import { useFeatureFlags } from '../FeatureFlagsContext';
import { FeatureFlags } from '../../flags';
import { toUrlFromQuery } from '../../utils';
import type { Session } from '../AuthContext';

interface UseEditAsOptions {
  /** API base URL */
  api: string;
  /** Tenant identifier */
  tenant: string;
  /** Whether tenant-level permissions are enabled */
  isUsingPermissions: boolean | undefined;
  /** Current authenticated session (null until loaded) */
  session: Session | null;
}

interface UseEditAsResult {
  /** Current edit-as agent identifier, or null */
  editAs: string | null;
  /** Set or clear the edit-as agent (triggers permission re-fetch) */
  setEditAs: (agentId: string | null) => void;
  /** True while /get-permissions is in-flight */
  isEditAsLoading: boolean;
  /** Error from the last /get-permissions call, or null */
  editAsError: Error | null;
  /** Effective permissions — edit-as overrides when active, otherwise session permissions */
  effectivePermissions: SystemPermissionResult;
}

/**
 * Manages the "edit as" (IAM) permission override flow.
 *
 * When an `iam` URL parameter is present and the NGW_EDIT_AS_FLOW feature flag
 * is enabled, fetches the target agent's permissions from /get-permissions and
 * returns them as `effectivePermissions`. All permission check functions in
 * AuthContext read from effectivePermissions, so the override is transparent
 * to every consumer.
 *
 * Exposes `setEditAs` for future in-app dynamic switching.
 */
export function useEditAs({
  api,
  tenant,
  isUsingPermissions,
  session,
}: UseEditAsOptions): UseEditAsResult {
  const { hasFeature } = useFeatureFlags();
  const isEditAsFlowEnabled = hasFeature(FeatureFlags.NGW_EDIT_AS_FLOW);

  const [editAs, setEditAsState] = useState<string | null>(null);
  const [editAsPermissions, setEditAsPermissions] =
    useState<SystemPermissionResult | null>(null);
  const [isEditAsLoading, setIsEditAsLoading] = useState(false);
  const [editAsError, setEditAsError] = useState<Error | null>(null);

  // Clear local edit-as state when the gating flags drop. URL→state sync runs
  // outside this hook (see EditAsUrlSync) because AuthProvider sits above the
  // Router; this hook just owns the fetch lifecycle.
  useEffect(() => {
    if (!isEditAsFlowEnabled || !isUsingPermissions) {
      setEditAsState(null);
    }
  }, [isEditAsFlowEnabled, isUsingPermissions]);

  // Fetch edit-as permissions when editAs changes
  useEffect(() => {
    if (!editAs || !session || !isEditAsFlowEnabled || !isUsingPermissions) {
      if (!editAs) {
        setEditAsPermissions(null);
        setEditAsError(null);
      }
      return;
    }

    let cancelled = false;

    const fetchPermissions = async () => {
      setIsEditAsLoading(true);
      setEditAsError(null);
      try {
        const url = toUrlFromQuery(`${api}/get-permissions`, {
          system: tenant,
          agent: editAs,
        });
        const response = await fetch(url, { credentials: 'include' });
        if (!response.ok) {
          throw new Error(
            `Failed to fetch edit-as permissions: ${response.status}`,
          );
        }
        const result: SystemPermissionResult = await response.json();
        if (!cancelled) {
          setEditAsPermissions(result);
        }
      } catch (err) {
        if (!cancelled) {
          setEditAsError(
            err instanceof Error
              ? err
              : new Error('Failed to fetch edit-as permissions'),
          );
          setEditAsPermissions(null);
        }
      } finally {
        if (!cancelled) {
          setIsEditAsLoading(false);
        }
      }
    };

    fetchPermissions();
    return () => {
      cancelled = true;
    };
  }, [editAs, session, api, tenant, isEditAsFlowEnabled, isUsingPermissions]);

  // Public setter — clears override state when null
  const setEditAs = useCallback((agentId: string | null) => {
    setEditAsState(agentId);
    if (!agentId) {
      setEditAsPermissions(null);
      setEditAsError(null);
    }
  }, []);

  // Effective permissions: edit-as overrides session permissions
  const effectivePermissions: SystemPermissionResult = useMemo(() => {
    if (editAsPermissions) {
      return editAsPermissions;
    }
    return {
      permissions: session?.permissions ?? [],
      permissionMap: session?.permissionMap ?? {},
      permissionKeyMap: session?.permissionKeyMap ?? {},
    };
  }, [editAsPermissions, session]);

  return {
    editAs,
    setEditAs,
    isEditAsLoading,
    editAsError,
    effectivePermissions,
  };
}
